import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Clock } from "lucide-react";
import { NAV_LINKS } from "../data/clinic";
import { scrollToId, scrollToTop } from "../lib/scroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), 60);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-sa-cream/80 backdrop-blur-xl border-b border-sa-espresso/10"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          onClick={scrollToTop}
          data-testid="nav-logo"
          className="font-display text-2xl sm:text-[26px] font-500 tracking-tight text-sa-espresso flex items-baseline gap-1.5"
        >
          MS<span className="text-sa-bronze italic">Pet Hospital</span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="font-body text-sm font-medium text-sa-mocha hover:text-sa-espresso transition-colors duration-300 relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-sa-bronze transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-1.5 text-sa-espresso">
            <Clock className="w-4 h-4 text-sa-bronze" />
            <span className="font-body text-sm font-semibold">Open 24/7</span>
          </div>
          <button
            onClick={() => handleNav("#booking")}
            data-testid="nav-book-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-sa-bronze px-6 py-2.5 font-body text-sm font-semibold text-sa-cream hover:bg-sa-bronzedark hover:scale-[1.03] active:scale-95 transition-[transform,background-color] duration-300"
          >
            Book Appointment
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 text-sa-espresso"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-sa-cream/95 backdrop-blur-xl border-b border-sa-espresso/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className="font-body text-left text-lg font-medium text-sa-espresso"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => handleNav("#booking")}
                data-testid="nav-mobile-book-btn"
                className="mt-2 rounded-full bg-sa-bronze px-6 py-3 font-body text-sm font-semibold text-sa-cream text-center"
              >
                Book Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
